import { Button, Flex, Grid, HStack, Stack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { StyledScrollWrapper } from '$/components/common/StyledScrollWrapper';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { EmptyStateCard } from '$/components/core/EmptyStateCard';
import { useIsMobile } from '$/hooks/useIsMobile';
import { trackDesignGuideCustomPageView, trackEvent } from '$/logger';
import { TooltipHeader } from '$/pages/DashboardPages/components/TooltipHeader';
import { HarmonyCard } from '$/pages/DashboardPages/pages/DesignGuide/components/HarmonyCard';
import { HarmonyFilters } from '$/pages/DashboardPages/pages/DesignGuide/components/HarmonyFilters';
import { useFilteredHarmonies } from '$/pages/DashboardPages/pages/DesignGuide/hooks/useFilteredHarmonies';
import { useDesignGuideStore } from '$/pages/DashboardPages/pages/DesignGuide/stores/useDesignGuideStore';
import { type Harmony } from '$/services/usecases/materials/mapper/harmonies';
import { usePiwikStore } from '$/stores/usePiwikStore';
import { materialTemplateColumn } from '$/theme/utils/templateColumn';

export const DesignGuidePage: FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const onOpenAuthModal = useAuthenticationStore.useOnModalOpen();

  const activeScene = useDesignGuideStore.useSelection().scene;
  const setSingle = useDesignGuideStore.useSetSingleFilter();

  const { items, hasPermission } = useFilteredHarmonies();

  const handleClick = useCallback(
    (harmony: Harmony) => {
      usePiwikStore.getState().setHarmonieName(harmony.name);
      trackEvent('Harmony', 'Click', harmony.name);

      void navigate({
        to: `/inspirations/${harmony.inspirationId}`,
        search: { harmonyId: harmony.id },
      });
    },
    [navigate],
  );

  return (
    <Stack
      w='full'
      {...(!isMobile && {
        layerStyle: 'DashboardSection',
      })}
    >
      <StyledScrollWrapper>
        <Stack
          gap='6'
          overflowY='auto'
          w='full'
          minH='calc(100dvh - 14rem)'
          {...(!isMobile && {
            p: '7',
          })}
        >
          <TooltipHeader header={t('dashboard.header.designGuide')} />

          <HStack align='stretch' gap='0' w='full'>
            <Button
              isActive={activeScene === 'exterior'}
              onClick={() => {
                if (activeScene === 'exterior') return;
                setSingle('scene', 'exterior');
                trackDesignGuideCustomPageView('exterior');
              }}
              variant='tabIndicator'
            >
              {t('dashboard.designGuide.facets.scene.exterior')}
            </Button>
            <Button
              isActive={activeScene === 'interior'}
              onClick={() => {
                if (activeScene === 'interior') return;
                setSingle('scene', 'interior');
                trackDesignGuideCustomPageView('interior');
              }}
              variant='tabIndicator'
            >
              {t('dashboard.designGuide.facets.scene.interior')}
            </Button>
          </HStack>

          <HarmonyFilters />

          {hasPermission && (
            <Grid
              gap={{ base: '3', md: '6' }}
              templateColumns={materialTemplateColumn()}
            >
              {items.map((harmony) => (
                <HarmonyCard
                  key={harmony.id}
                  harmony={harmony}
                  onClick={handleClick}
                />
              ))}
            </Grid>
          )}

          {!hasPermission && (
            <Flex align='center' justify='center' flex='1' minH='0'>
              <EmptyStateCard
                icon='folder'
                title={t('dashboard.designGuide.empty.title')}
                description={t('dashboard.designGuide.empty.description')}
                buttonText={t('dashboard.designGuide.empty.action')}
                onClick={() => onOpenAuthModal('login')}
              />
            </Flex>
          )}
        </Stack>
      </StyledScrollWrapper>
    </Stack>
  );
};
